import React, { useContext, useMemo } from "react";
import {
  Checkbox,
  InputNumber,
  Form,
  Typography,
  Row,
  Col,
  Input,
  Button,
  theme,
} from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import {
  pastMedicalOptions,
  controlOptions,
  nutritionistPastMedicalOptions,
} from "../../../Options/historyOptions";
import DeselectableRadioGroup from "../../../../../blocks/FormItems/DeselectableRadioGroup";
import { hexToRgba } from "../../../../../helpers/utility";

const { Text } = Typography;

const PastMedicalSinceYearsInput = ({ style, ...props }) => (
  <div className="past-medical-since-yrs-field" style={style}>
    <span className="past-medical-since-prefix">Since</span>
    <InputNumber
      className="past-medical-years-input"
      min={0}
      max={80}
      step={0.1}
      stringMode
      {...props}
    />
    <span className="past-medical-since-suffix">yrs</span>
  </div>
);

// ✅ Same pattern as DynamicInputFields but with name + yrs + control UI
const DynamicOthersFields = ({ form, token, hexToRgba }) => {
  const getFieldName = () => ["past_medical_others_name"];

  return (
    <Form.List
      name={getFieldName()}
      initialValue={[{ name: "", yrs: "", control: "" }]}
    >
      {(fields, { add, remove }) => {
        const handleAdd = () => {
          const formValues = form.getFieldValue(getFieldName());
          const lastField = formValues?.[formValues.length - 1];
          if (lastField?.name) {
            add({ name: "", yrs: "", control: "" });
          }
        };

        return (
          <>
            {fields.map((field, index) => (
              <div
                key={field.key}
                style={{
                  borderLeft: `2px solid ${hexToRgba(
                    token.colorPrimaryHover,
                    0.2
                  )}`,
                  padding: "8px 12px",
                  borderRadius: 8,
                  marginBottom: 8,
                }}
              >
                <Row
                  gutter={[8, 8]}
                  className="past-medical-others-row"
                >
                  <Col xs={24} lg={9} className="past-medical-others-name-col">
                    <Form.Item {...field} name={[field.name, "name"]} noStyle>
                      <Input
                        className="past-medical-others-name-input"
                        placeholder="Enter condition name"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Row gutter={[8, 8]} align="middle" className="past-medical-detail-row">
                      <Col xs={24} lg={10}>
                        <Form.Item name={[field.name, "yrs"]} noStyle>
                          <PastMedicalSinceYearsInput />
                        </Form.Item>
                      </Col>

                      <Col xs={24} lg={14}>
                        <DeselectableRadioGroup
                          name={[field.name, "control"]}
                          form={form}
                          options={controlOptions}
                        />
                      </Col>
                    </Row>
                  </Col>

                  {index !== 0 && (
                    <Col xs={24} className="past-medical-others-remove">
                      <Button
                        icon={<MinusOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    </Col>
                  )}
                </Row>
              </div>
            ))}

            {/* Add button */}
            <Form.Item>
              <Button type="dashed" icon={<PlusOutlined />} onClick={handleAdd}>
                Add Other
              </Button>
            </Form.Item>
          </>
        );
      }}
    </Form.List>
  );
};

const PastMedicalHistory = ({ context, form, nutritionist = false }) => {
  const { token } = theme.useToken();
  const { formData } = useContext(context);
  const historyData = formData?.history || formData;
  const watchedValues = Form.useWatch([], form);

  const optionsList = nutritionist
    ? nutritionistPastMedicalOptions
    : pastMedicalOptions;

  const checkboxes = useMemo(() => {
    const source = { ...historyData, ...(watchedValues || {}) };
    return optionsList.reduce((acc, checkbox) => {
      acc[checkbox.value] = !!source?.[checkbox.value];
      return acc;
    }, {});
  }, [historyData, watchedValues, optionsList]);

  const handleCheckboxChange = (value, event) => {
    const nextChecked = event?.target?.checked ?? !checkboxes?.[value];

    if (value === "past_medical_others") {
      if (nextChecked) {
        const current = form?.getFieldValue?.(["past_medical_others_name"]);
        if (!current?.length) {
          form?.setFieldValue?.(["past_medical_others_name"], [
            { name: "", yrs: "", control: "" },
          ]);
        }
      } else {
        form?.setFieldValue?.(["past_medical_others_name"], []);
      }
    }
  };

  const midIndex = Math.ceil(optionsList.length / 2);
  const leftOptions = optionsList.slice(0, midIndex);
  const rightOptions = optionsList.slice(midIndex);

  return (
    <>
      <Text>Past Medical History</Text>

      <Row gutter={[24, 16]}>
        {[leftOptions, rightOptions].map((columnOptions, colIndex) => (
          <Col xs={24} sm={12} key={colIndex}>
            {columnOptions.map((checkbox) => (
              <div key={checkbox.value} style={{ marginBottom: "10px" }}>
                <Form.Item
                  name={checkbox.value}
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox
                    onChange={(e) => handleCheckboxChange(checkbox.value, e)}
                  >
                    {checkbox.label}
                  </Checkbox>
                </Form.Item>

                {checkboxes?.[checkbox.value] && (
                  <div
                    style={{
                      borderLeft: `2px solid ${hexToRgba(
                        token.colorPrimaryHover,
                        0.2
                      )}`,
                      background: "transparent",
                      marginTop: "8px",
                      padding: "8px 12px",
                      borderRadius: "8px",
                    }}
                  >
                    {/* ✅ Others — use DynamicOthersFields */}
                    {checkbox.value === "past_medical_others" ? (
                      <DynamicOthersFields
                        form={form}
                        token={token}
                        hexToRgba={hexToRgba}
                      />
                    ) : (
                      // Normal row for all other checkboxes
                      <Row gutter={[8, 8]} className="past-medical-detail-row">
                        <Col xs={24} lg={10}>
                          <Form.Item name={`${checkbox.value}_yrs`}>
                            <PastMedicalSinceYearsInput />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={14}>
                          <DeselectableRadioGroup
                            name={`${checkbox.value}_control`}
                            form={form}
                            options={controlOptions}
                          />
                        </Col>
                      </Row>
                    )}
                  </div>
                )}
              </div>
            ))}
          </Col>
        ))}
      </Row>
    </>
  );
};

export default PastMedicalHistory;
